import * as React from 'react';

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import type { BookingDraft, ServiceMappers, Services, ServiceType } from '@/scopes/checkout/domain/types';
import { createAncillariesAddRemoveEvent } from '@/scopes/checkout/domain/analytics/index';

interface Dependencies {
  serviceMappers: ServiceMappers;
}

export const useSendAncillaryAddRemove = (dependencies: Partial<Dependencies>) => {
  const { sendEvent } = useAnalytics();
  const { serviceMappers } = dependencies as Dependencies;

  const sendAncillaryAddRemoveEvent = React.useCallback(
    <K extends ServiceType>(service: Services[K], serviceType: K, bookingDraft: BookingDraft) => {
      if (!service) return;

      const previousService = bookingDraft.services[serviceType];
      const serviceMapper = serviceMappers[serviceType];
      const checkoutProps = bookingDraft.analytics;

      const { ancillaryAddMapped, ancillaryRemoveMapped } = serviceMapper.mapAncillaryAddedAndRemovedToGA4Result(
        service,
        previousService
      );
      if (ancillaryRemoveMapped) {
        ancillaryRemoveMapped.forEach((ancillary) => {
          sendEvent(createAncillariesAddRemoveEvent(ancillary, 'ancillary_remove', 'remove', checkoutProps));
        });
      }
      if (ancillaryAddMapped) {
        ancillaryAddMapped.forEach((ancillary) => {
          sendEvent(createAncillariesAddRemoveEvent(ancillary, 'ancillary_add', 'add', checkoutProps));
        });
      }
    },
    [serviceMappers, sendEvent]
  );

  return {
    sendAncillaryAddRemoveEvent,
  };
};
